import { captureFinancialWorkIntentFromLocation } from '@/lib/financialWork/intent'
import { tryResolveMarketApiServerUrl } from '@/lib/marketApiServerUrl'

// API Configuration
// Base API URL (without /swagger/json)
export const API_BASE_URL: string = process.env.NEXT_PUBLIC_API_BASE_URL ?? ''

// Get auth token from localStorage (client-side) or cookie (server-side)
function getAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    // Client-side: committed session, or pending Billin onboarding token
    const token = localStorage.getItem('auth_token')
    if (token?.trim()) return token.trim()
    try {
      const pending = sessionStorage.getItem('signin_pending_auth_token')
      return pending?.trim() ? pending.trim() : null
    } catch {
      return null
    }
  }
  return null
}

function hasFinancialWorkHarnessNoAuth(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('fwHarnessNoAuth') === '1'
  } catch {
    return false
  }
}

// Handle session expiration - clear auth data and redirect to login
function handleSessionExpiration(): void {
  // Only run on client-side
  if (typeof window === 'undefined') {
    return
  }
  if (hasFinancialWorkHarnessNoAuth()) {
    return
  }

  const pathname = window.location.pathname
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const isPublicPage =
    normalizedPath === '/' ||
    normalizedPath === '/login' ||
    normalizedPath === '/signin' ||
    normalizedPath === '/signin/external' ||
    normalizedPath === '/login/aera' ||
    normalizedPath === '/login/aera/external' ||
    normalizedPath === '/privacy' ||
    normalizedPath === '/terms-of-use' ||
    normalizedPath === '/credits/topup' ||
    normalizedPath === '/support' ||
    normalizedPath.startsWith('/support/')

  // Prevent redirect loops and allow public pages to stay accessible.
  if (isPublicPage) {
    // /signin Billin onboarding — keep pending session so workspace step can recover
    if (normalizedPath === '/signin') {
      try {
        const onboardingActive =
          sessionStorage.getItem('signin_billin_onboarding_active_v1') === '1'
        if (onboardingActive) {
          return
        }
      } catch {
        // ignore
      }
    }
    // Just clear data, don't reload/redirect
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    try {
      sessionStorage.removeItem('signin_pending_auth_token')
      sessionStorage.removeItem('signin_pending_user')
      sessionStorage.removeItem('signin_billin_onboarding_active_v1')
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new Event('auth:logout'))
    } catch {
      // ignore
    }
    return
  }

  console.warn('🔴 Session expired - redirecting to home page')

  // LIFF-00: keep opaque Financial Work object across re-login when query is dropped.
  let financialWorkReturnPath: string | null = null
  try {
    const search = window.location.search || ''
    const returnPath = `${pathname}${search}`
    if (captureFinancialWorkIntentFromLocation()) {
      financialWorkReturnPath = returnPath
    }
  } catch {
    // ignore — never block logout on intent helpers
  }
  
  // Clear authentication data
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user')
  try {
    sessionStorage.removeItem('signin_pending_auth_token')
    sessionStorage.removeItem('signin_pending_user')
    sessionStorage.removeItem('signin_billin_onboarding_active_v1')
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new Event('auth:logout'))
  } catch {
    // ignore
  }
  
  // Prefer sign-in with callback so LIFF returns to the same work object.
  if (financialWorkReturnPath) {
    window.location.href = `/signin?callbackUrl=${encodeURIComponent(financialWorkReturnPath)}&session_expired=true`
    return
  }

  // Redirect to home (same behavior as explicit logout flows).
  // Keep a small flag for optional UX messaging.
  window.location.href = `/?session_expired=true`
}

/** Extra options for apiFetch (stripped before passing to `fetch`). */
export type ApiFetchOptions = RequestInit & {
  nullOn404?: boolean
  /** If true, call the target URL directly from the browser (skip `/api/proxy`). Requires CORS on the API. */
  skipProxy?: boolean
}

/**
 * Prefer nested slip / validation text (e.g. `POST .../report-payment` errors) over bare `error` codes.
 */
export function pickUserFacingErrorMessage(
  data: Record<string, unknown> | null | undefined,
): string | null {
  if (!data || typeof data !== 'object') return null

  const slipTextFrom = (obj: Record<string, unknown>): string | null => {
    const slip = obj.slipDetail
    if (typeof slip !== 'string' || !slip.trim()) return null
    const t = slip.trim()
    if (t.startsWith('{')) {
      try {
        const j = JSON.parse(t) as { detail?: string }
        if (typeof j.detail === 'string' && j.detail.trim()) return j.detail.trim()
      } catch {
        return t
      }
    }
    return t
  }

  // `details.slipDetail` from API, or nested under `/api/proxy` axios `{ details: upstreamBody }`.
  let cur: unknown = data
  for (let depth = 0; depth < 4 && cur && typeof cur === 'object'; depth++) {
    const layer = cur as Record<string, unknown>
    const slip = slipTextFrom(layer)
    if (slip) return slip
    cur = layer.details
  }

  const innerErr = (data.details as Record<string, unknown> | undefined)?.error
  if (typeof innerErr === 'string' && innerErr.trim()) return innerErr.trim()

  const top = data.error
  if (typeof top === 'string' && top.trim()) return top.trim()
  const msg = data.message
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  return null
}

// Global request deduplication map
const pendingRequests = new Map<string, Promise<any>>();

function isCrossOriginAbsoluteUrl(url: string): boolean {
  if (typeof window === 'undefined') return false
  if (!url.startsWith('http')) return false
  try {
    const targetOrigin = new URL(url).origin
    return targetOrigin !== window.location.origin
  } catch {
    return false
  }
}

// API Fetch wrapper with deduplication
export async function apiFetch<T = unknown>(
  endpoint: string,
  options?: ApiFetchOptions
): Promise<T> {
  const method = options?.method || 'GET';
  
  // Only deduplicate GET requests on client-side
  if (method === 'GET' && typeof window !== 'undefined') {
    // Create a reasonably unique key for the request
    // We can't fully resolve URL here easily without duplicating logic, 
    // so we use the endpoint + stringified options as a proxy key.
    // This is safe because same endpoint with same options = same request.
    
    /* 
       Note: We are not normalizing the URL here (e.g. handling BASE_URL), 
       so apiFetch('/foo') and apiFetch('http://.../foo') are treated as different keys.
       This is acceptable for deduplication purposes.
    */
    const paramsKey = options ? JSON.stringify(options) : '';
    const deDupeKey = `GET:${endpoint}:${paramsKey}`;

    if (pendingRequests.has(deDupeKey)) {
       return pendingRequests.get(deDupeKey) as Promise<T>;
    }

    const promise = _internalApiFetch<T>(endpoint, options).finally(() => {
       pendingRequests.delete(deDupeKey);
    });

    pendingRequests.set(deDupeKey, promise);
    return promise;
  }

  return _internalApiFetch<T>(endpoint, options);
}

// API Fetch utility function
// Internal API Fetch implementation
async function _internalApiFetch<T = unknown>(
  endpoint: string,
  options?: ApiFetchOptions
): Promise<T> {
  const nullOn404 = Boolean(options?.nullOn404)
  const skipProxy = Boolean(options?.skipProxy)
  const fetchOptions: RequestInit | undefined = options
    ? (() => {
        const { nullOn404: _n, skipProxy: _s, ...rest } =
          options as ApiFetchOptions
        return rest
      })()
    : undefined

  let url: string
  if (endpoint.startsWith('http')) {
    url = endpoint
  } else if (endpoint.startsWith('/api/')) {
    // Next.js API routes - use current origin (for client-side) or construct from request (for server-side)
    if (typeof window !== 'undefined') {
      url = endpoint // Browser will use relative URL
    } else {
      // Server-side: we need to construct the full URL
      // This is a fallback - ideally this should be passed as a parameter
      const origin = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'
      url = `${origin}${endpoint}`
    }
  } else {
    // Remove trailing slash from API_BASE_URL if present
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    url = `${baseUrl}${normalizedEndpoint}`
  }

  // Next server cannot hairpin to the public market-api HTTPS IP.
  if (typeof window === 'undefined' && url.startsWith('http')) {
    url = tryResolveMarketApiServerUrl(url)
  }

  // Get auth token
  const token = getAuthToken()


  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions?.headers as Record<string, string> || {}),
  }
  
  // Add Authorization header if token exists
  if (token) {
    // Ensure token doesn't already have "Bearer " prefix
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token
    headers['Authorization'] = `Bearer ${cleanToken}`
  }

  const shouldProxy =
    typeof window !== 'undefined' &&
    !skipProxy &&
    isCrossOriginAbsoluteUrl(url) &&
    // Avoid proxying Next.js API routes (already same-origin)
    !endpoint.startsWith('/api/')



  let response!: Response
  const maxFetchAttempts = typeof window !== "undefined" ? 2 : 1
  try {
    let lastFetchError: unknown
    for (let attempt = 1; attempt <= maxFetchAttempts; attempt++) {
      try {
        const fetchPromise = shouldProxy
          ? fetch('/api/proxy', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                url,
                method: fetchOptions?.method || 'GET',
                headers,
                data: fetchOptions?.body,
              }),
            })
          : fetch(url, {
              ...fetchOptions,
              headers,
            })
        response = await fetchPromise
        break
      } catch (e) {
        lastFetchError = e
        if (attempt === maxFetchAttempts) {
          throw lastFetchError
        }
        console.warn(`[ApiFetch] fetch attempt ${attempt} failed; retrying once…`, {
          url,
          method: fetchOptions?.method || 'GET',
        })
      }
    }
  } catch (fetchError) {
    // Handle network errors (Failed to fetch)
    const errorMessage = fetchError instanceof Error ? fetchError.message : String(fetchError)
    // Use warn (not error): Next.js dev overlay treats `console.error` as a surfaced runtime issue.
    // `Failed to fetch` is often environmental (LIFF webview, adblock, DNS, API down) and we rethrow below.
    console.warn('Network request failed (fetch):', {
      url,
      method: fetchOptions?.method || 'GET',
      error: errorMessage,
      possibleCauses: [
        'Backend API server is not running',
        'CORS issue - backend might not allow requests from this origin',
        'Network connectivity issue',
        'Invalid URL or DNS resolution failed',
        'SSL/TLS certificate issue',
      ],
      urlDetails: {
        isAbsolute: url.startsWith('http'),
        isRelative: url.startsWith('/'),
        origin: typeof window !== 'undefined' ? window.location.origin : 'server-side',
      },
    })
    
    // Create a more descriptive error
    const networkError = new Error(`Network Error: ${errorMessage}. Please check if the backend API is running at ${API_BASE_URL}`) as Error & { 
      data?: unknown
      isNetworkError?: boolean
    }
    networkError.data = { 
      url, 
      originalError: errorMessage,
      apiBaseUrl: API_BASE_URL,
    }
    networkError.isNetworkError = true
    throw networkError
  }



  if (!response.ok) {
    /** "No resource" is valid for some GETs (e.g. order by workspace+service). */
    if (response.status === 404 && nullOn404) {
      return null as T
    }
    const errorText = await response.text()
    let errorMessage = `API Error: ${response.status} ${response.statusText}`
    let errorData: Record<string, unknown> | null = null
    
    try {
      errorData = JSON.parse(errorText)
      errorMessage =
        pickUserFacingErrorMessage(errorData) ||
        (errorData?.error as string) ||
        (errorData?.message as string) ||
        errorMessage
    } catch {
      if (errorText) {
        errorMessage = errorText
      }
    }
    
    // Enhanced logging for 401 errors
    if (response.status === 401 && typeof window !== 'undefined') {
      console.error("🔴 401 Unauthorized Error Details:", {
        url,
        method: fetchOptions?.method || 'GET',
        hasToken: !!token,
        tokenLength: token?.length || 0,
        authorizationHeader: headers['Authorization'] ? 'Bearer ***' : 'Missing',
        responseText: errorText,
        errorData,
        possibleCauses: [
          "Token might be expired",
          "Token format might be incorrect",
          "Backend might not recognize the token",
          "Token might not be stored in backend database",
          "Backend secret key might have changed",
          "CORS or header issues"
        ]
      })
      
      // Handle session expiration - clear auth and redirect to login
      handleSessionExpiration()
      // Throw error to stop execution (redirect will happen)
      throw new Error('Session expired')
    }
    
    // Hairpin / upstream down: do not `console.error` — Next overlay treats that as a page error.
    if (/ECONNREFUSED/i.test(errorMessage)) {
      console.warn("Upstream connection refused:", { url, errorMessage, status: response.status })
      const networkError = new Error(errorMessage) as Error & {
        data?: unknown
        status?: number
        isNetworkError?: boolean
      }
      networkError.data = errorData || { error: errorMessage }
      networkError.status = response.status
      networkError.isNetworkError = true
      throw networkError
    }

    // Create error object that includes the error data
    const error = new Error(errorMessage) as Error & { data?: unknown; status?: number }
    error.data = errorData || { error: errorMessage }
    error.status = response.status
    throw error
  }

  let responseData
  try {
    responseData = await response.json()
  } catch {
    // Response is not JSON, try to get text
    const text = await response.text()
    console.error("❌ Failed to parse response as JSON:", text)
    throw new Error(`Invalid response format: ${text.substring(0, 100)}`)
  }
  
  // Check if response data itself contains an error
  // Some APIs return 200 OK but with error field in response body
  if (responseData && typeof responseData === 'object' && responseData.error) {
    const errorMessage =
      pickUserFacingErrorMessage(responseData as Record<string, unknown>) ||
      (typeof responseData.error === 'string'
        ? responseData.error
        : (responseData.message as string)) ||
      'An error occurred'
    
    // Unauthorized in JSON body (some endpoints return 200 + { error: "Unauthorized" })
    if (errorMessage.includes('Unauthorized') && typeof window !== 'undefined') {
      const currentToken = getAuthToken()
      if (currentToken) {
        console.error("🔴 Unauthorized Error in Response Body:", {
          httpStatus: response.status,
          errorMessage,
          hasToken: true,
          tokenLength: currentToken.length,
          tokenPreview: currentToken.substring(0, 50) + '...',
          tokenInfo: (() => {
            try {
              const parts = currentToken.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                return {
                  userId: payload.userId || payload.sub || payload.id,
                  exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'No expiration',
                  iat: payload.iat ? new Date(payload.iat * 1000).toISOString() : 'No issued at',
                  isExpired: payload.exp ? Date.now() > payload.exp * 1000 : 'Unknown'
                }
              }
            } catch {
              return { decodeError: 'Cannot decode token' }
            }
            return null
          })(),
          fullResponse: responseData,
          suggestion: "Token might be invalid, expired, or backend doesn't recognize it. Try logging in again."
        })
        handleSessionExpiration()
      }
      // No token: expected for guest calls to protected APIs — do not clear session or spam errors
    }

    const error = new Error(errorMessage) as Error & { data?: unknown }
    error.data = responseData
    throw error
  }
  
  return responseData
}

// GET request helper
export async function apiGet<T = unknown>(endpoint: string, options?: ApiFetchOptions): Promise<T> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'GET',
  })
}

// POST request helper
export async function apiPost<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestInit
): Promise<T> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })
}

// PUT request helper
export async function apiPut<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestInit
): Promise<T> {
  
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  })
}

// PATCH request helper
export async function apiPatch<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: RequestInit
): Promise<T> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  })
}

// DELETE request helper
export async function apiDelete<T = unknown>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'DELETE',
  })
}

// Upload file helper (for multipart/form-data)
export async function apiUpload<T = unknown>(
  endpoint: string,
  formData: FormData,
  options?: RequestInit,
  onProgress?: (percent: number) => void
): Promise<T> {
  let url: string
  if (endpoint.startsWith('http')) {
    url = endpoint
  } else if (endpoint.startsWith('/api/')) {
    // Next.js API routes - use current origin (for client-side) or construct from request (for server-side)
    if (typeof window !== 'undefined') {
      url = endpoint // Browser will use relative URL
    } else {
      // Server-side: we need to construct the full URL
      const origin = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'
      url = `${origin}${endpoint}`
    }
  } else {
    // Remove trailing slash from API_BASE_URL if present
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    url = `${baseUrl}${normalizedEndpoint}`
  }

  if (typeof window === 'undefined' && url.startsWith('http')) {
    url = tryResolveMarketApiServerUrl(url)
  }

  // Get auth token
  const token = getAuthToken()
  
  // If onProgress is provided, use XMLHttpRequest for progress tracking
  if (onProgress && typeof window !== 'undefined') {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      
      // Add Authorization header if token exists
      if (token) {
        const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token
        xhr.setRequestHeader('Authorization', `Bearer ${cleanToken}`)
      }
      
      // Add any custom headers from options
      if (options?.headers) {
        const customHeaders = options.headers as Record<string, string>
        Object.keys(customHeaders).forEach(key => {
          xhr.setRequestHeader(key, customHeaders[key])
        })
      }
      
      // Upload progress handler
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          onProgress(percent)
        }
      }
      
      xhr.onload = () => {
        let responseData: any
        try {
          responseData = JSON.parse(xhr.responseText)
        } catch (e) {
          console.error("❌ Failed to parse upload response as JSON:", xhr.responseText)
          reject(new Error(`Invalid response format: ${xhr.responseText.substring(0, 100)}`))
          return
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          // Check for error in success response
          if (responseData && typeof responseData === 'object' && responseData.error) {
             const errorMessage = typeof responseData.error === 'string' 
              ? responseData.error 
              : (responseData.message as string) || 'An error occurred'
            const error = new Error(errorMessage) as Error & { data?: unknown }
            error.data = responseData
            reject(error)
          } else {
            resolve(responseData as T)
          }
        } else {
          // Handle error response
          if (xhr.status === 401) {
            console.error('🔴 401 Unauthorized during file upload - session expired')
            handleSessionExpiration()
            reject(new Error('Session expired'))
            return
          }
          
          let errorMessage = `API Error: ${xhr.status} ${xhr.statusText}`
          
          // Extract error message
          const extractedMessage = (responseData?.message as string) || 
                                   (responseData?.error as string) || 
                                   errorMessage
                                   
           // Check if there's a suggestion in details
          const details = responseData?.details as Record<string, unknown> | undefined
          const suggestion = details?.suggestion as string | undefined
          
          if (suggestion) {
            errorMessage = `${extractedMessage}\n\n💡 ${suggestion}`
          } else {
            errorMessage = extractedMessage
          }
          
          const error = new Error(errorMessage) as Error & { data?: unknown }
          error.data = responseData
          reject(error)
        }
      }
      
      xhr.onerror = () => {
        reject(new Error('Network request failed'))
      }
      
      xhr.send(formData)
    })
  }

  // Fallback to fetch if no progress callback or server-side
  const headers: Record<string, string> = {
    // Don't set Content-Type header - let browser set it automatically with boundary for multipart/form-data
    ...(options?.headers as Record<string, string> || {}),
  }
  
  // Add Authorization header if token exists
  if (token) {
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token
    headers['Authorization'] = `Bearer ${cleanToken}`
  }

  const response = await fetch(url, {
    ...options,
    method: 'POST',
    headers,
    body: formData,
  })

  if (!response.ok) {
    // Handle 401 Unauthorized - session expired
    if (response.status === 401 && typeof window !== 'undefined') {
      console.error('🔴 401 Unauthorized during file upload - session expired')
      handleSessionExpiration()
      throw new Error('Session expired')
    }
    
    const errorText = await response.text()
    let errorMessage = `API Error: ${response.status} ${response.statusText}`
    let errorData: Record<string, unknown> | null = null
    
    try {
      errorData = JSON.parse(errorText)
      
      // Extract error message with priority: message > error > default
      const extractedMessage = (errorData?.message as string) || 
                                (errorData?.error as string) || 
                                errorMessage
      
      // Check if there's a suggestion in details
      const details = errorData?.details as Record<string, unknown> | undefined
      const suggestion = details?.suggestion as string | undefined
      
      // Build error message with suggestion if available
      if (suggestion) {
        errorMessage = `${extractedMessage}\n\n💡 ${suggestion}`
      } else {
        errorMessage = extractedMessage
      }
      
      // Log detailed error for debugging
      if (typeof window !== 'undefined') {
        console.error("❌ Upload API Error Details:", {
          status: response.status,
          statusText: response.statusText,
          errorMessage: extractedMessage,
          suggestion,
          errorData,
          errorText,
          fullResponse: errorData,
        })
      }
    } catch {
      if (errorText) {
        errorMessage = errorText
      }
      
      // Log raw error text
      if (typeof window !== 'undefined') {
        console.error("❌ Upload API Error (non-JSON):", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        })
      }
    }
    
    const error = new Error(errorMessage) as Error & { data?: unknown }
    error.data = errorData || { error: errorMessage }
    throw error
  }

  let responseData
  try {
    responseData = await response.json()
  } catch {
    // Response is not JSON, try to get text
    const text = await response.text()
    console.error("❌ Failed to parse upload response as JSON:", text)
    throw new Error(`Invalid response format: ${text.substring(0, 100)}`)
  }
  
  // Check if response data itself contains an error
  if (responseData && typeof responseData === 'object' && responseData.error) {
    const errorMessage = typeof responseData.error === 'string' 
      ? responseData.error 
      : (responseData.message as string) || 'An error occurred'
    
    const error = new Error(errorMessage) as Error & { data?: unknown }
    error.data = responseData
    throw error
  }
  
  return responseData
}

